/**
 * Extracts field names that have validation errors from an error object
 * @param errorObject - Object containing validation error properties
 * @returns Array of field names with errors (without 'ValidationError' suffix)
 */
export const extractFieldsWithErrors = (errorObject: any): string[] => {
    return Object.keys(errorObject)
        .filter(key => key.endsWith('ValidationError') && errorObject[key])
        .map(key => key.replace('ValidationError', ''));
};

/**
 * Processes machine-level errors including nested variant, cluster and criteria errors
 */
export const extractMachineErrors = (machineListErrors: any) => {
    const fieldsWithErrorList: string[][] = [];
    const variantWithErrorList: string[][] = [];
    const clusterWithErrorList: string[][] = [];
    const criteriaWithErrorList: string[][] = [];

    for (let machineIndex = 0; machineIndex < machineListErrors.length; machineIndex++) {
        const machineEntry = machineListErrors[machineIndex] || {};
        const variantErrors = machineEntry.variantListErrors || [];

        fieldsWithErrorList.push(extractFieldsWithErrors(machineEntry));
        variantWithErrorList.push([]);
        clusterWithErrorList.push([]);
        criteriaWithErrorList.push([]);

        for (let variantIndex = 0; variantIndex < variantErrors.length; variantIndex++) {
            const variantEntry = variantErrors[variantIndex] || {};
            const variantFieldErrors = extractFieldsWithErrors(variantEntry);

            if (variantFieldErrors.length > 0) {
                variantWithErrorList[machineIndex].push(`Variant ${variantIndex + 1}`);
            }

            const clusterErrors = variantEntry.clusterListErrors || [];
            for (let clusterIndex = 0; clusterIndex < clusterErrors.length; clusterIndex++) {
                const clusterEntry = clusterErrors[clusterIndex] || {};
                const clusterFieldErrors = extractFieldsWithErrors(clusterEntry);

                if (clusterFieldErrors.length > 0) {
                    clusterWithErrorList[machineIndex].push(
                        `Cluster ${clusterIndex + 1} of Variant ${variantIndex + 1}`
                    );
                }

                const selectionCriteriaListErrors = clusterEntry.selectionCriteriaListErrors || [];
                for (let criteriaIndex = 0; criteriaIndex < selectionCriteriaListErrors.length; criteriaIndex++) {
                    const criteriaEntry = selectionCriteriaListErrors[criteriaIndex] || {};
                    const criteriaFieldErrors = extractFieldsWithErrors(criteriaEntry);

                    if (criteriaFieldErrors.length > 0) {
                        criteriaWithErrorList[machineIndex].push(
                            `Selection Criteria ${criteriaIndex + 1} of Cluster ${clusterIndex + 1} of Variant ${variantIndex + 1}`
                        );
                    }
                }
            }
        }
    }

    return {
        fieldsWithErrorList,
        variantWithErrorList,
        clusterWithErrorList,
        criteriaWithErrorList
    };
};

/**
 * Extracts cluster errors for a single variant
 */
const extractClusterErrorsForVariant = (
    clusterListErrors: any[],
    variantIndex: number,
    clusterWithErrorList: string[][],
    criteriaWithErrorList: string[][]
) => {
    for (let clusterIndex = 0; clusterIndex < clusterListErrors.length; clusterIndex++) {
        const clusterEntry = clusterListErrors[clusterIndex];
        const clusterFieldErrors = extractFieldsWithErrors(clusterEntry);

        if (clusterFieldErrors.length > 0) {
            clusterWithErrorList[variantIndex].push(`Cluster ${clusterIndex + 1}`);
        }

        extractCriteriaErrorsForCluster(
            clusterEntry.selectionCriteriaListErrors,
            clusterIndex,
            variantIndex,
            criteriaWithErrorList
        );
    }
};

/**
 * Extracts criteria errors for a single cluster
 */
const extractCriteriaErrorsForCluster = (
    selectionCriteriaListErrors: any[],
    clusterIndex: number,
    variantIndex: number,
    criteriaWithErrorList: string[][]
) => {
    for (let criteriaIndex = 0; criteriaIndex < selectionCriteriaListErrors.length; criteriaIndex++) {
        const criteriaEntry = selectionCriteriaListErrors[criteriaIndex];
        const criteriaFieldErrors = extractFieldsWithErrors(criteriaEntry);

        if (criteriaFieldErrors.length > 0) {
            criteriaWithErrorList[variantIndex].push(
                `Selection Criteria ${criteriaIndex + 1} of Cluster ${clusterIndex + 1}`
            );
        }
    }
};

/**
 * Processes variant-level errors including nested cluster and criteria errors
 */
export const extractVariantErrors = (machineListErrors: any, machineIndex: number) => {
    const fieldsWithErrorList: string[][] = [];
    const clusterWithErrorList: string[][] = [];
    const criteriaWithErrorList: string[][] = [];

    const variantErrors = machineListErrors[machineIndex].variantListErrors;

    for (let variantIndex = 0; variantIndex < variantErrors.length; variantIndex++) {
        clusterWithErrorList.push([]);
        criteriaWithErrorList.push([]);

        const variantEntry = variantErrors[variantIndex];
        const variantFieldErrors = extractFieldsWithErrors(variantEntry);
        fieldsWithErrorList.push(variantFieldErrors);

        if (variantEntry.clusterListErrors) {
            extractClusterErrorsForVariant(
                variantEntry.clusterListErrors,
                variantIndex,
                clusterWithErrorList,
                criteriaWithErrorList
            );
        }
    }

    return { fieldsWithErrorList, clusterWithErrorList, criteriaWithErrorList };
};

/**
 * Extracts criteria errors for a single cluster (used by extractClusterErrors)
 */
const extractCriteriaErrorsForSingleCluster = (
    selectionCriteriaListErrors: any[],
    clusterIndex: number,
    criteriaWithErrorList: string[][]
) => {
    if (!selectionCriteriaListErrors?.length) return;

    for (let criteriaIndex = 0; criteriaIndex < selectionCriteriaListErrors.length; criteriaIndex++) {
        const criteriaEntry = selectionCriteriaListErrors[criteriaIndex];
        const criteriaFieldErrors = extractFieldsWithErrors(criteriaEntry);

        if (criteriaFieldErrors.length > 0) {
            criteriaWithErrorList[clusterIndex].push(`Selection Criteria ${criteriaIndex + 1}`);
        }
    }
};

/**
 * Processes cluster-level errors including nested criteria errors
 */
export const extractClusterErrors = (
    machineListErrors: any,
    machineIndex: number,
    variantIndex: number
) => {
    const fieldsWithErrorList: string[][] = [];
    const criteriaWithErrorList: string[][] = [];

    const clusterErrors = machineListErrors[machineIndex].variantListErrors[variantIndex].clusterListErrors;

    for (let clusterIndex = 0; clusterIndex < clusterErrors.length; clusterIndex++) {
        criteriaWithErrorList.push([]);

        const clusterEntry = clusterErrors[clusterIndex];
        const clusterFieldErrors = extractFieldsWithErrors(clusterEntry);
        fieldsWithErrorList.push(clusterFieldErrors);

        extractCriteriaErrorsForSingleCluster(
            clusterEntry.selectionCriteriaListErrors,
            clusterIndex,
            criteriaWithErrorList
        );
    }

    return { fieldsWithErrorList, criteriaWithErrorList };
};

/**
 * Processes selection criteria errors
 */
export const extractSelectionCriteriaErrors = (
    machineListErrors: any,
    machineIndex: number,
    variantIndex: number,
    clusterIndex: number
) => {
    const fieldsWithErrorList: string[][] = [];

    const criteriaErrors = machineListErrors[machineIndex]
        .variantListErrors[variantIndex]
        .clusterListErrors[clusterIndex]
        .selectionCriteriaListErrors;

    for (const criteriaEntry of criteriaErrors) {
        const criteriaFieldErrors = extractFieldsWithErrors(criteriaEntry);
        fieldsWithErrorList.push(criteriaFieldErrors);
    }

    return { fieldsWithErrorList };
};